import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import { createHyphenatedString } from "../../../../formatting/string-manipulation";
import type { ReferenceDisplay, SectionProps, SectionRow } from "../../types";
import SectionFields from "./SectionFields";

function referencesInOrder(
  references: Array<ReferenceDisplay>,
): Array<ReferenceDisplay> {
  return [...references].sort(
    (firstReference, secondReference) =>
      firstReference.index - secondReference.index,
  );
}

const referenceContainerSx = {
  minWidth: 0,
  width: "100%",
  "& > *": { maxWidth: "100%" },
  "& img": { display: "block", maxWidth: "100%", height: "auto" },
};

const fieldsWithReferenceColumns = {
  xs: "minmax(0, 1fr)",
  lg: "minmax(0, 1.25fr) minmax(320px, 1fr)",
};

function SectionRows({
  rows,
  columns,
  sectionId,
}: {
  rows: Array<SectionRow>;
  columns: number;
  sectionId: string;
}) {
  return (
    <Stack spacing={4}>
      {rows.map((row, rowIndex) => (
        <Box
          key={`${sectionId}-row-${rowIndex}`}
          data-testid={`${sectionId}-section-row-${rowIndex}`}
          sx={{
            display: "grid",
            gridTemplateColumns: fieldsWithReferenceColumns,
            gap: 3,
            alignItems: "start",
          }}
        >
          <SectionFields
            fields={row.fields}
            columns={columns}
            fieldIdPrefix={sectionId}
          />
          <Box
            data-testid={`${sectionId}-section-row-${rowIndex}-reference`}
            sx={{
              ...referenceContainerSx,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {row.reference}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

function Section({
  title,
  columns,
  fields = [],
  reference = [],
  rows,
  onEdit,
}: SectionProps) {
  const sectionId = createHyphenatedString(title);
  const orderedReferences = referencesInOrder(reference);
  const rightReferences = orderedReferences.filter(
    (referenceItem) => referenceItem.place === "right",
  );
  const beneathReferences = orderedReferences.filter(
    (referenceItem) => referenceItem.place === "beneath",
  );
  const hasRightReferences = rightReferences.length > 0;

  const renderReferences = (references: Array<ReferenceDisplay>) => (
    <Stack spacing={3} alignItems="center" sx={referenceContainerSx}>
      {references.map((referenceItem) => (
        <Box
          key={`${referenceItem.index}-${referenceItem.type}-${referenceItem.place}`}
          data-testid={`${sectionId}-section-reference-${referenceItem.index}`}
          data-reference-type={referenceItem.type}
          data-reference-place={referenceItem.place}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: 0,
            width: "100%",
          }}
        >
          {referenceItem.item}
        </Box>
      ))}
    </Stack>
  );

  return (
    <Paper
      elevation={3}
      data-testid={`${sectionId}-section`}
      sx={{ m: 4, position: "relative" }}
    >
      <Box sx={{ p: 4 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Typography data-testid={sectionId} variant="h5" align="left">
            {title}
          </Typography>
          {onEdit && (
            <IconButton
              aria-label={`Edit ${title}`}
              data-testid={`${sectionId}-edit-button`}
              onClick={onEdit}
              size="small"
            >
              <ModeEditIcon />
            </IconButton>
          )}
        </Stack>

        {rows ? (
          <SectionRows rows={rows} columns={columns} sectionId={sectionId} />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: hasRightReferences
                ? fieldsWithReferenceColumns
                : "minmax(0, 1fr)",
              gap: 3,
              alignItems: "center",
            }}
          >
            <SectionFields
              fields={fields}
              columns={columns}
              fieldIdPrefix={sectionId}
            />

            {hasRightReferences && (
              <Box
                data-testid="section-references-right"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minWidth: 0,
                  width: "100%",
                }}
              >
                {renderReferences(rightReferences)}
              </Box>
            )}

            {beneathReferences.length > 0 && (
              <Box
                data-testid="section-references-beneath"
                sx={{ gridColumn: "1 / -1", minWidth: 0 }}
              >
                {renderReferences(beneathReferences)}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default Section;
